import * as isomorphicLocalStorage from 'lib/storage/isomorphicLocalStorage'

export interface PageViewAnalyticsPriceShareData {
  price?: number;
}

const ANALYTICS_KEY_PREFIX = 'analytics_data::'
const ANALYTICS_DATA_TTL = 60 * 60 // 1 hour

export const ANALYTICS_DATA_TYPE = {
  PRICE: 'price_data',
} as const

export type DataKey = typeof ANALYTICS_DATA_TYPE[keyof typeof ANALYTICS_DATA_TYPE]
export type StorageKey = `${typeof ANALYTICS_KEY_PREFIX}${string}::${DataKey}`
export type StorageData<T> = T & {
  expireTimestamp: number
}

export function getAnalyticsDataStorageKey(currentPath?: string, dataKey?: DataKey): StorageKey | undefined {
  if (!currentPath || !dataKey) {
    return
  }

  return `${ANALYTICS_KEY_PREFIX}${currentPath}::${dataKey}`
}

export function set<T>(key?: StorageKey, value?: T) {
  if (!key || !value) {
    return
  }

  const expireTimestamp = Date.now() + ANALYTICS_DATA_TTL * 1000

  const storageData: StorageData<T> = {
    ...value,
    expireTimestamp,
  }

  isomorphicLocalStorage.set(key, storageData)
}

/**
 * @param key - The key to get the value from the storage. Must be a valid storage key created by getDataKey function.
 * @returns The value from the storage or undefined if the value is not found
 */
export function get<T>(key?: StorageKey): T | undefined {
  if (!key) {
    return
  }

  try {
    const item: StorageData<T> | null = isomorphicLocalStorage.get(key)

    if (item) {
      const { expireTimestamp = 0, ...value } = item
      const now = Date.now()
      if (expireTimestamp <= now) {
        // remove but still return the value
        isomorphicLocalStorage.remove(key)
      }

      return value as T
    }

    return undefined
  } catch {
    return undefined
  }
}
